import React, { PureComponent } from 'react';
import Config from 'Config';
import { RouteComponentProps } from 'react-router';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Utils
import getWindow from 'Utils/get-window';
import { GA } from 'Utils/analytics';
import Debouncer from 'Utils/debouncer';
import sentenceCase from 'Utils/content/sentence-case';
import { getCookieValue } from 'Utils/client-cookie';
import { styleObject, componentSelector } from 'Style/type-helpers';

// Components
import Button from 'Webapp/shared/app/components/button';
import Input from 'Webapp/shared/app/components/base/input';
import Message from 'ComponentLibrary/typography/message';
import AppleLogin from 'Webapp/shared/app/components/auth/sso-logins/apple-login';
import GoogleLogin from 'Webapp/shared/app/components/auth/sso-logins/google-login';

import StrongPasswordInput from 'Webapp/shared/app/components/auth/strong-password-input';
import TermsOfUseAndPrivacyPolicy from 'Webapp/shared/app/components/auth/terms-of-use-and-privacy-policy';
import AuthModule from 'Webapp/shared/app/components/auth/auth-module';
import WebLink from 'Webapp/shared/app/components/web-link';
import FrictionlessForm, {
  MainForm,
} from 'Webapp/shared/app/components/frictionless-form';

import styled from '@emotion/styled';
import { BREAKPOINTS } from 'Style/breakpoints';
import { TEXT_COLORS, SURFACE_COLORS } from 'Style/colors';
import { SPACING } from 'Style/spacing';
import { UI_TEXT_TYPES, UI_HEADING_TYPES } from 'Style/typography';
import FlipboardLogo from 'ComponentLibrary/logos/flipboard';
import PoundBulletIcon from 'ComponentLibrary/icons/pound-bullet';
import PlusBulletIcon from 'ComponentLibrary/icons/plus-bullet';
import ShareBulletIcon from 'ComponentLibrary/icons/share-bullet';

import { FrictionlessFlowType, Services } from 'Webapp/enums';

import connector from 'Utils/connector';
import connectRouting, {
  ConnectRoutingProps,
} from 'Webapp/shared/app/connectors/connectRouting';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';
import { SignupFormContainerProps } from 'Webapp/shared/app/containers/auth/signup-form';

import withReCaptcha from 'Webapp/shared/app/hocs/withReCaptcha';
import withT from 'ComponentLibrary/hocs/withT';
import withHistory from 'Webapp/shared/app/hocs/withHistory';

const SignupModule = styled(AuthModule)(
  styleObject(BREAKPOINTS.tabletPortraitUp({ width: '720px', padding: 0 })),
);
const SignupWrapper = styled.div(
  {
    justifyContent: 'center',
  },
  styleObject(BREAKPOINTS.tabletPortraitUp({ display: 'flex' })),
);
const SignupEdu = styled.div(
  {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexBasis: '300px',
    paddingTop: SPACING.LARGE,
  },
  styleObject(
    BREAKPOINTS.tabletPortraitUp({
      padding: `${SPACING.BASE10X} 0`,
      background: SURFACE_COLORS.secondary,
    }),
  ),
);
const Logo = styled(FlipboardLogo)(
  {
    marginBottom: SPACING.BASE6X,
  },
  styleObject(
    BREAKPOINTS.phone({
      marginBottom: SPACING.MEDIUM,
    }),
  ),
);
const SignupTitle = styled.h1(
  UI_TEXT_TYPES.PAGE_TITLE,
  {
    marginBottom: SPACING.BASE,
  },
  styleObject(BREAKPOINTS.phone(UI_HEADING_TYPES.MEDIUM)),
);
const SignupTagline = styled.p(
  UI_TEXT_TYPES.BODY,
  styleObject(
    BREAKPOINTS.tabletPortraitUp({
      marginBottom: SPACING.BASE10X,
    }),
  ),
);

const SignupEduExtra = styled.div(
  styleObject(BREAKPOINTS.phone({ display: 'none' })),
);

const SignupReasons = styled.div({
  display: 'inline-block',
  flexDirection: 'column',
  justifyContent: 'space-evenly',
  margin: '0 auto',
});
const SignupReason = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  marginBottom: SPACING.MEDIUM,
});
const SignupEduDefaultText = styled.p(UI_TEXT_TYPES.SUPPORTING, {
  marginLeft: SPACING.BASE,
  color: TEXT_COLORS.primary,
});

const SignupModalContents = styled.div(
  {
    padding: `${SPACING.BASE10X} ${SPACING.BASE6X} ${SPACING.XLARGE} ${SPACING.BASE6X}`,
  },
  styleObject(
    BREAKPOINTS.phone({
      padding: `${SPACING.LARGE} 0`,
    }),
  ),
);

const StyledFrictionlessForm = styled(FrictionlessForm)({
  padding: `${SPACING.LARGE} 0`,
  [componentSelector(MainForm)]: {
    gap: SPACING.XLARGE,
  },
});
const SignupForm = styled.form({
  display: 'grid',
  gap: SPACING.LARGE,
});

const SignupSocialButtonsWrapper = styled.div({
  marginBottom: SPACING.BASE4X,
  display: 'flex',
  justifyContent: 'center',
  gap: SPACING.MEDIUM,
});

const SignupAltMessage = styled.p(UI_TEXT_TYPES.SUPPORTING, {
  marginBottom: SPACING.BASE,
});

const SignupAltActions = styled.div(
  {
    color: TEXT_COLORS.tertiary,
    margin: `0 0 ${SPACING.LARGE} 0 `,
  },
  UI_TEXT_TYPES.SUPPORTING,
);

const AltAction = styled.p({
  marginBottom: SPACING.BASE4X,
  a: { fontWeight: 'unset', textDecoration: 'underline' },
});

interface SignupState {
  isRecaptchaOn: boolean;
  isErrorRecaptcha: boolean;
  signupSuccess: boolean;
  service: Services | null;
}
interface SignupPassedProps {
  className?: string;
  redirectAfterAuth?: boolean;
  // comes from app/modals/signup.js
  onComplete?: () => void;
}

type SignupProps = ConnectRoutingProps &
  ConnectFeatureFlagsProps &
  SignupFormContainerProps &
  SignupPassedProps & {
    // comes from withT
    t: Flipboard.TFunction;
    // comes from withHistory
    history: RouteComponentProps['history'];
    // comes from withRecaptcha
    validateReCaptcha: (
      captchaType: string,
      cb: () => Promise<void>,
    ) => Promise<void>;
    ReCaptchaUI: JSX.Element;
    isReCaptchaPending: boolean;
  };

class Signup extends PureComponent<SignupProps, SignupState> {
  constructor(props: SignupProps) {
    super(props);
    this.debouncedPasswordCheck = Debouncer.create(
      this.props.getPasswordStrength,
      800,
    ) as (password: string) => void;

    this.state = {
      isRecaptchaOn: false,
      isErrorRecaptcha: false,
      signupSuccess: false,
      service: null,
    };
  }

  componentDidMount() {
    const { usageTrackSignupEnter, routing } = this.props;
    GA.trackPageview(getWindow().document.title, routing.path);
    usageTrackSignupEnter();
  }

  componentWillUnmount() {
    const { usageTrackSignupExit } = this.props;
    const { signupSuccess, service } = this.state;
    usageTrackSignupExit(signupSuccess, service);
  }

  debouncedPasswordCheck: (password: string) => void;

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.authFormUpdate({ [e.target.name]: e.target.value });
  };

  handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.handleChange(e);
    if (!Config.ENABLE_PASSWORD_VALIDATION) {
      return null;
    }
    this.debouncedPasswordCheck(e.target.value);
  };

  handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { validateReCaptcha } = this.props;
    await validateReCaptcha('signup', this.handleSignup);
  };

  handleSignupSuccess = async (service: Services) => {
    const { populateAuthentication, history, onComplete, redirectAfterAuth } =
      this.props;
    this.setState({ signupSuccess: true, service });

    GA.trackStandardSignupExit(true, service);

    try {
      await populateAuthentication();

      if (redirectAfterAuth) {
        const redirectPath = getCookieValue('redirect_after_auth');
        // Server-rendered routes (e.g. the /oauth/* consent flow) are not client
        // React routes, so a history.push lands on the SPA catch-all ("No Content")
        // and the Express handler never runs. Hard-navigate those. onComplete is
        // intentionally not called here: it continues the normal signup onboarding
        // (continueOnboardingFlow), which the OAuth consent flow supersedes — the
        // user came to authorize, not to finish onboarding — and this full page load
        // discards SPA state anyway.
        if (redirectPath && redirectPath.startsWith('/oauth/')) {
          getWindow().location = redirectPath;
          return;
        }
        history.push(redirectPath || '/');
      }
      if (onComplete) {
        onComplete();
      }
    } catch (e) {
      getWindow().location = '/?action=onboard';
    }
  };

  handleSignup = async () => {
    const {
      authForm: { realName, email, password },
      userSignup,
    } = this.props;
    const response = await userSignup(realName, email, password);
    if (response.error) {
      GA.trackStandardSignupExit(false);
      return;
    }
    await this.handleSignupSuccess(Services.FLIPBOARD);
  };

  handleSignupWithSSOToken = async (service: Services, token: string) => {
    const { connectWithSSO, setSignupCompleted } = this.props;
    await connectWithSSO(service, token);
    setSignupCompleted();
    await this.handleSignupSuccess(service);
  };

  isFormValid = () => {
    const {
      authForm: { realName, email, password, passwordStrength },
    } = this.props;
    if (!realName || !email || !password) {
      return false;
    }
    if (Config.ENABLE_PASSWORD_VALIDATION) {
      return (
        passwordStrength &&
        passwordStrength.strength >=
          Config.PASSWORD_STRENGTH_MINIMUM_REQUIRED_LEVEL
      );
    }
    return true;
  };

  standardFlowForm = () => {
    const {
      authForm: {
        realName,
        email,
        password,
        passwordStrength,
        isLoading,
        isLoadingPasswordStrength,
        errorMessage,
      },
      t,
      isReCaptchaPending,
      ReCaptchaUI,
    } = this.props;
    return (
      <React.Fragment>
        {passwordStrength &&
          passwordStrength.strength <
            Config.PASSWORD_STRENGTH_MINIMUM_REQUIRED_LEVEL && (
            <Message>{passwordStrength.reason}</Message>
          )}
        <Message>{errorMessage}</Message>
        <SignupForm onSubmit={this.handleSubmit}>
          <Input
            type="email"
            name="email"
            placeholder={t('email')}
            value={email}
            onChange={this.handleChange}
            disabled={isLoading}
          />
          <Input
            type="text"
            name="realName"
            placeholder={t('full_name')}
            value={realName}
            onChange={this.handleChange}
            disabled={isLoading}
          />
          <StrongPasswordInput
            value={password}
            passwordStrength={passwordStrength}
            onChange={this.handlePasswordChange}
            disabled={isLoading}
          />
          <Button
            name="signup__form-submit"
            className="button--primary button--block"
            type="submit"
            disabled={
              !this.isFormValid() ||
              isReCaptchaPending ||
              isLoadingPasswordStrength ||
              isLoading
            }
            loading={isLoadingPasswordStrength || isLoading}
          >
            {sentenceCase(t('continue'))}
          </Button>
        </SignupForm>
        {ReCaptchaUI}
      </React.Fragment>
    );
  };

  renderForm = () => {
    const { featureFlags } = this.props;
    return featureFlags.FRICTIONLESS_SIGNUP_ONLY ? (
      <StyledFrictionlessForm
        frictionlessType={FrictionlessFlowType.MAIN_SIGNUP}
        submitStringOverride="continue"
      />
    ) : (
      this.standardFlowForm()
    );
  };

  render() {
    const { setSSOError, className, t, logoSize } = this.props;
    return (
      <SignupModule>
        <SignupWrapper>
          <SignupEdu>
            <Logo size={logoSize} />
            <SignupTitle>{t('signup_join_flipboard')}</SignupTitle>
            <SignupTagline>
              {t('signup_stay_informed_stay_inspired')}
            </SignupTagline>
            <SignupEduExtra>
              <SignupReasons>
                <SignupReason>
                  <PoundBulletIcon />
                  <SignupEduDefaultText>
                    {t('signup_follow_topics')}
                  </SignupEduDefaultText>
                </SignupReason>
                <SignupReason>
                  <PlusBulletIcon />
                  <SignupEduDefaultText>
                    {t('signup_curate_stories')}
                  </SignupEduDefaultText>
                </SignupReason>
                <SignupReason>
                  <ShareBulletIcon />
                  <SignupEduDefaultText>
                    {t('signup_share_ideas')}
                  </SignupEduDefaultText>
                </SignupReason>
              </SignupReasons>
            </SignupEduExtra>
          </SignupEdu>
          <SignupModalContents className={className}>
            {this.renderForm()}
            <SignupAltMessage>{t('or_sign_up_with')}</SignupAltMessage>
            <SignupSocialButtonsWrapper>
              <AppleLogin
                onAuth={this.handleSignupWithSSOToken}
                onError={setSSOError}
              />
              <GoogleOAuthProvider clientId={Config.GOOGLE_SDK_APP_ID}>
                <GoogleLogin
                  onAuth={this.handleSignupWithSSOToken}
                  onError={setSSOError}
                />
              </GoogleOAuthProvider>
            </SignupSocialButtonsWrapper>
            <SignupAltActions>
              <AltAction>
                <WebLink href="/publisher_signup">
                  {t('get_started_as_a_publisher')}
                </WebLink>
              </AltAction>
              <AltAction>
                {t('already_have_an_account')}
                <br />
                <WebLink name="signup__login-link" href="/login">
                  {t('log_in')}
                </WebLink>
              </AltAction>
            </SignupAltActions>
            <TermsOfUseAndPrivacyPolicy />
          </SignupModalContents>
        </SignupWrapper>
      </SignupModule>
    );
  }
}

export default connector(
  connectRouting,
  connectFeatureFlags,
)(withHistory(withReCaptcha(withT(Signup))));
